import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import AddressField from "./AddressField";
import DateField from "./DateField";
import { SlidersHorizontal, X, Bookmark, RotateCcw } from "lucide-react";

const inputCls = "border border-[#E8E4D9] focus:border-[#1C1B1A] rounded-full text-xs bg-white px-3 py-2 outline-none transition-colors";

export function useFilterParams() {
  const [params, setParams] = useSearchParams();
  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    const apply = (key, val) => {
      if (val === "" || val === null || val === undefined) next.delete(key);
      else next.set(key, String(val));
    };
    if (typeof k === "object") Object.entries(k).forEach(([key, val]) => apply(key, val));
    else apply(k, v);
    setParams(next);
  };
  const clearAll = (keep = []) => {
    const next = new URLSearchParams();
    keep.forEach((k) => { if (params.get(k)) next.set(k, params.get(k)); });
    setParams(next);
  };
  return [params, setParam, clearAll];
}

function FilterField({ def, params, setParam }) {
  const v = params.get(def.key) || "";
  if (def.type === "select") {
    return (
      <select data-testid={`filter-${def.key}`} value={v} onChange={(e) => setParam(def.key, e.target.value)} className={`${inputCls} ${def.className || ""}`}>
        <option value="">{def.label}</option>
        {def.options.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
      </select>
    );
  }
  if (def.type === "date") {
    return <DateField value={v} onChange={(val) => setParam(def.key, val)} testId={`filter-${def.key}`} placeholder={def.label} className="!py-2 !text-xs" />;
  }
  if (def.type === "number") {
    return <input data-testid={`filter-${def.key}`} type="number" min="0" value={v} placeholder={def.label}
      onChange={(e) => setParam(def.key, e.target.value)} className={`${inputCls} w-24`} />;
  }
  if (def.type === "near") {
    return (
      <div className="min-w-[190px]">
        <AddressField value={{ address: params.get("near_label") || "" }}
          onSelect={(r) => setParam({ near_lat: r.lat, near_lng: r.lng, near_label: r.label })}
          testId="filter-near" placeholder={def.label} />
      </div>
    );
  }
  return <input data-testid={`filter-${def.key}`} value={v} placeholder={def.label}
    onChange={(e) => setParam(def.key, e.target.value)} className={`${inputCls} ${def.className || "w-36"}`} />;
}

export function activeFilters(fields, params) {
  const out = [];
  fields.forEach((f) => {
    const v = params.get(f.key);
    if (!v) return;
    let label = v;
    if (f.type === "select") label = f.options.find(([val]) => val === v)?.[1] || v;
    out.push({ key: f.key, label: `${f.short || f.label}: ${label}` });
  });
  if (params.get("near_label") && params.get("radius_km")) {
    out.push({ key: "__near", label: `Within ${params.get("radius_km")} km`, clear: { near_lat: null, near_lng: null, near_label: null, radius_km: null } });
  }
  return out;
}

export default function FiltersBar({ fields, params, setParam, clearAll, sortOptions, total,
  saved = [], onSaveView, onApplySaved, onDeleteSaved }) {
  const [open, setOpen] = useState(false);
  const active = activeFilters(fields, params);

  const controls = (stacked = false) => (
    <>
      {fields.map((f) => (
        <div key={f.key} className={stacked ? "w-full" : ""}>
          {stacked && <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400 mb-1">{f.label}</p>}
          <div className={stacked ? "[&_input]:w-full [&_select]:w-full [&>div]:min-w-0" : ""}>
            <FilterField def={f} params={params} setParam={setParam} />
          </div>
        </div>
      ))}
      {sortOptions && (
        <div className={stacked ? "w-full" : ""}>
          {stacked && <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400 mb-1">Sort</p>}
          <select data-testid="filter-sort" value={params.get("sort") || ""} onChange={(e) => setParam("sort", e.target.value)} className={`${inputCls} ${stacked ? "w-full" : ""}`}>
            <option value="">Newest first</option>
            {sortOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      )}
    </>
  );

  return (
    <div className="border-b border-white/60 bg-white/80 backdrop-blur-xl px-4 sm:px-6 py-2.5" data-testid="filters-bar">
      <div className="hidden md:flex flex-wrap items-center gap-2">{controls()}</div>

      <div className="md:hidden flex items-center gap-2">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="filters-open-btn" className="flex items-center gap-1.5 border border-[#E8E4D9] rounded-full px-3.5 py-2 text-xs font-semibold">
              <SlidersHorizontal className="w-3.5 h-3.5" /> Filters{active.length > 0 && ` (${active.length})`}
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto">
            <div className="w-10 h-1 bg-zinc-200 rounded-full mx-auto mb-5" />
            <div className="flex flex-col gap-4 pb-4">{controls(true)}</div>
            <button data-testid="filters-apply-btn" onClick={() => setOpen(false)}
              className="w-full bg-[#1C1B1A] text-white font-medium py-3 rounded-full text-sm mt-2">
              Show {total != null ? `${total} ` : ""}results
            </button>
          </SheetContent>
        </Sheet>
        {total != null && <span className="text-xs text-zinc-400" data-testid="filters-total">{total} result{total !== 1 ? "s" : ""}</span>}
      </div>

      {active.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="active-filter-chips">
          {active.map((c) => (
            <span key={c.key} className="inline-flex items-center gap-1 bg-white border border-[#E8E4D9] rounded-full pl-2.5 pr-1 py-0.5 text-[11px] font-medium">
              {c.label}
              <button data-testid={`chip-clear-${c.key}`} onClick={() => c.clear ? setParam(c.clear) : setParam(c.key, null)}
                className="w-4 h-4 rounded-full hover:bg-zinc-300 flex items-center justify-center"><X className="w-2.5 h-2.5" /></button>
            </span>
          ))}
          <button data-testid="filters-reset-btn" onClick={() => clearAll()} className="inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-500 hover:text-black px-2 py-0.5">
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
          {onSaveView && (
            <button data-testid="save-view-btn" onClick={onSaveView} className="inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-500 hover:text-black px-2 py-0.5">
              <Bookmark className="w-3 h-3" /> Save view
            </button>
          )}
        </div>
      )}

      {saved.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          <span className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold">Saved:</span>
          {saved.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1 border border-zinc-200 rounded-full pl-2.5 pr-1 py-0.5 text-[11px]">
              <button data-testid={`saved-view-${s.id}`} onClick={() => onApplySaved(s)} className="font-medium hover:underline">{s.name}</button>
              <button data-testid={`saved-view-del-${s.id}`} onClick={() => onDeleteSaved(s)} className="w-4 h-4 rounded-full hover:bg-zinc-200 flex items-center justify-center"><X className="w-2.5 h-2.5" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
